import React from 'react';
import type { CompetitionRulesSummary } from '../../../src/competitions/types/competitions.types';
import { bracketStages } from '../../../src/competitions/helpers/tournamentBracket.helper';
import { TournamentStandingsScreen } from '../TournamentStandings/TournamentStandingsScreen';
import { TournamentBracketScreen } from '../TournamentBracket/TournamentBracketScreen';
import { CompetitionStandingsModalView } from './CompetitionStandingsModalView';
import type { CompetitionStandingsModalView as ModalView } from './competitionStandingsModal.constants';

export interface CompetitionStandingsModalProps {
  isVisible: boolean;
  onDismiss: () => void;
  /** Server-resolved rules; decides which panes this competition has earned. */
  rules?: CompetitionRulesSummary | null;
  initialView?: ModalView;
  testID?: string;
}

/**
 * Table | Bracket for a generic tournament, wired to the two existing screens
 * (specs/champions-league, Stories 5 & 6, revised).
 *
 * The screens are mounted as-is rather than reimplemented: each already owns its query, its
 * pick saver and its boost hook, so a prediction made in the bracket here is the same pick the
 * Picks tab shows. They are mounted **lazily** — only while the modal is open — so a player who
 * never opens it never pays for the standings and matches queries.
 */
export const CompetitionStandingsModal: React.FC<CompetitionStandingsModalProps> = ({
  isVisible,
  onDismiss,
  rules,
  initialView,
  testID,
}) => {
  if (!isVisible) return null;

  const hasTable = rules?.hasTournamentStandings === true;
  const hasBracket = bracketStages(
    rules?.stageOrder ?? [], rules?.multiRoundStages ?? {},
  ).length > 0;

  return (
    <CompetitionStandingsModalView
      isVisible={isVisible}
      onDismiss={onDismiss}
      table={hasTable ? <TournamentStandingsScreen /> : undefined}
      bracket={hasBracket ? <TournamentBracketScreen /> : undefined}
      initialView={initialView}
      testID={testID}
    />
  );
};
